/**
 * A list of the drawing's targets as a vertical side rail beside the canvas.
 *
 * These began as a horizontal chip strip in the context band, which forced a
 * real drawing's 150+ targets into a single scrolling line where only a handful
 * were ever legible. A vertical list shows twenty at a time, gives each one room
 * for its subtitle, and leaves the horizontal axis to the drawing.
 */
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleX, Eye, Pencil, Search, TriangleAlert } from 'lucide-react';
import { AppIcon } from '../components/LucideIcon';
import type { TargetRailGroup, TargetRailItem } from './targetRailItems';
import './workspace-target-rail.css';

/** The search box's rule: the label or the sub line contains the query. */
export function matchesRailQuery(item: Pick<TargetRailItem, 'label' | 'sub'>, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return item.label.toLowerCase().includes(needle) || (item.sub || '').toLowerCase().includes(needle);
}

/** One checkbox under the search box: its owner limits the list to the rows it names. */
function RailFilterCheck({ label, filter }: {
  label: string;
  filter: { checked: boolean; onChange: (next: boolean) => void; count: number };
}) {
  return (
    <label className="pnid-target-check">
      <input
        type="checkbox"
        checked={filter.checked}
        onChange={(event) => filter.onChange(event.target.checked)}
      />
      <span>{label} ({filter.count})</span>
    </label>
  );
}

export default function WorkspaceTargetRail({
  title,
  items,
  activeId,
  onSelect,
  onCenter,
  onEdit,
  panelId,
  labelledBy,
  groups,
  header,
  tools,
  keepEmpty = false,
  emptyMessage,
  query: controlledQuery,
  onQueryChange,
  needsReview,
  pendingIds,
  onReview,
  needsFix,
}: {
  /** A checkbox under the search box; its owner limits the list to the pending rows. `count` is the
   *  number of pending rows the list shows, or would show with the box on. */
  needsReview?: { checked: boolean; onChange: (next: boolean) => void; count: number };
  /** The rows an agent proposal waits on. Each wears a warning icon before its label, and with
   *  onReview its click opens that review instead of onSelect. Given, every row reserves the
   *  icon's width so the labels stay aligned. */
  pendingIds?: ReadonlySet<string>;
  onReview?: (id: string) => void;
  /** A second checkbox: its owner limits the list to the rows whose `needsFix` is set. Given, each
   *  such row wears a fix mark, and every row reserves the mark's width so the labels stay aligned. */
  needsFix?: { checked: boolean; onChange: (next: boolean) => void; count: number };
  /** The search box's value, when its owner needs it too (the asset rail keeps it across its tabs). */
  query?: string;
  onQueryChange?: (next: string) => void;
  /** Replaces the default empty text; a rail whose list has nowhere else to point. */
  emptyMessage?: ReactNode;
  panelId?: string;
  labelledBy?: string;
  groups?: TargetRailGroup[];
  header?: ReactNode;
  /** Controls that belong with the search box, rendered above it inside the tools block. */
  tools?: ReactNode;
  keepEmpty?: boolean;
  title: string;
  items: TargetRailItem[];
  activeId?: string | null;
  onSelect: (id: string) => void;
  /** With onEdit, gives each row an eye (center) and a pencil (edit) button. */
  onCenter?: (id: string) => void;
  onEdit?: (id: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  /* The rail replaced a dropdown that owned the search. Without it a drawing
     with 150+ targets is unnavigable, so the filter comes along. */
  const [ownQuery, setOwnQuery] = useState('');
  const query = controlledQuery ?? ownQuery;
  const setQuery = onQueryChange ?? setOwnQuery;
  const filtered = useMemo(() => items.filter((item) => matchesRailQuery(item, query)), [items, query]);

  /* The canvas and the Next control both move the selection, so the list has to
     follow it — otherwise the active row sits hundreds of pixels out of view. */
  useEffect(() => {
    if (!activeId) return;
    const active = listRef.current?.querySelector<HTMLElement>('.pnid-target-row--active');
    /* jsdom implements no scrollIntoView, and this is presentation only. */
    if (!active || typeof active.scrollIntoView !== 'function') return;
    active.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  if (!items.length && !keepEmpty) return null;

  return (
    <aside className="pnid-target-rail" aria-label={title}>
      {header}
      <div className="pnid-target-rail-tools">
        {tools}
        <div className="pnid-target-search">
          <AppIcon icon={Search} size={14} />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${title}`}
            aria-label={`Search ${title}`}
          />
        </div>
        {(needsReview || needsFix) && (
          <div className="pnid-target-checks">
            {needsReview && <RailFilterCheck label="Needs review" filter={needsReview} />}
            {needsFix && <RailFilterCheck label="Needs fix" filter={needsFix} />}
          </div>
        )}
      </div>
      <div className="pnid-target-review-body" id={panelId} role={panelId ? 'tabpanel' : undefined} aria-labelledby={labelledBy}>
        <div className="pnid-target-rail-list"
          ref={listRef} role={keepEmpty ? undefined : 'tablist'} aria-orientation={keepEmpty ? undefined : 'vertical'}>
          {!filtered.length && (
            <p className="pnid-target-empty">
              {items.length ? `No match for “${query}”.` : emptyMessage ?? `No ${title.toLowerCase()} to review.`}
            </p>
          )}
          {(groups ?? [{ id: title, label: '', items }]).map((group) => {
            const visibleItems = group.items.filter((item) => filtered.includes(item));
            if (!visibleItems.length) return null;
            return (
              <div key={group.id} className="pnid-target-group">
                {group.label && <h3 className="pnid-target-group-title">{group.label}<span>{visibleItems.length}</span></h3>}
                {visibleItems.map((item) => {
                  const active = item.id === activeId;
                  const pending = Boolean(pendingIds?.has(item.id));
                  return (
                    <div key={item.id} className="pnid-target-row-slot">
                      <button type="button"
                        role={keepEmpty ? undefined : 'tab'}
                        aria-selected={keepEmpty ? undefined : active}
                        aria-pressed={keepEmpty ? active : undefined}
                        className={[
                          'pnid-target-row',
                          active && 'pnid-target-row--active',
                          onCenter && onEdit && 'pnid-target-row--with-actions',
                        ].filter(Boolean).join(' ')}
                        onClick={() => (pending && onReview ? onReview(item.id) : onSelect(item.id))}
                        title={item.sub ? `${item.label} — ${item.sub}` : item.label}>
                        {pendingIds && (
                          <span className="pnid-target-pending-slot">
                            {pending && (
                              <span role="img" aria-label="Needs review" title="Proposed by the agent, needs review">
                                <AppIcon icon={TriangleAlert} size={14} />
                              </span>
                            )}
                          </span>
                        )}
                        {needsFix && (
                          <span className="pnid-target-fix-slot">
                            {/* DECISION(2026-10-28, abhishek): One mark for any finding: no count, no tooltip, no ok mark.
                                The SME opens the editor to read and act on the findings, so the rail only says where. */}
                            {item.needsFix && (
                              <span role="img" aria-label="Needs fix">
                                <AppIcon icon={CircleX} size={14} />
                              </span>
                            )}
                          </span>
                        )}
                        <span className="pnid-target-text">
                          <span className="pnid-target-label">{item.label}</span>
                          {item.sub && <span className="pnid-target-sub">{item.sub}</span>}
                        </span>
                      </button>
                      {onCenter && onEdit && (
                        <span className="pnid-target-row-actions">
                          <button type="button" className="pnid-target-row-action"
                            aria-label={`Center ${item.label}`} title="Center on drawing"
                            onClick={() => onCenter(item.id)}>
                            <AppIcon icon={Eye} size={14} />
                          </button>
                          <button type="button" className="pnid-target-row-action"
                            aria-label={`Edit ${item.label}`} title="Center and edit"
                            onClick={() => onEdit(item.id)}>
                            <AppIcon icon={Pencil} size={14} />
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
